import {
  Scene,
  Mesh,
  MeshBuilder,
  DynamicTexture,
  StandardMaterial,
  Vector3,
  Color3,
  Color4,
  InstancedMesh
} from 'babylonjs';
import { BUILDING_DEFINITIONS, BuildingType, UNIT_DEFINITIONS, UnitType } from '../shared/MessageType';

const FULL_VISION = true;

// Minimal interfaces matching game3d.ts structure
interface Unit3D {
  mesh: InstancedMesh;
  id: number;
  playerId: number;
  k: UnitType;
  x: number;
  y: number;
}

interface Building3D {
  mesh: InstancedMesh;
  id: number;
  playerId: number;
  k: BuildingType;
  x: number;
  y: number;
}

export enum VisibilityState {
  UNEXPLORED = 0,
  EXPLORED = 1,
  VISIBLE = 2
}


/**
 * Quadtree node for efficient spatial visibility storage
 */
class QuadtreeNode {
  public children: QuadtreeNode[] | null = null;
  public state: VisibilityState | null = null; // null means not uniform, check children
  public x: number;
  public y: number;
  public width: number;
  public height: number;

  constructor(x: number, y: number, width: number, height: number) {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
  }

  /**
   * Check if this node is a leaf (has no children)
   */
  public isLeaf(): boolean {
    return this.children === null;
  }

  /**
   * Split this node into 4 children
   */
  public split(): void {
    const halfWidth = this.width / 2;
    const halfHeight = this.height / 2;
    const currentState = this.state ?? VisibilityState.UNEXPLORED;

    this.children = [
      new QuadtreeNode(this.x, this.y, halfWidth, halfHeight),
      new QuadtreeNode(this.x + halfWidth, this.y, halfWidth, halfHeight),
      new QuadtreeNode(this.x, this.y + halfHeight, halfWidth, halfHeight),
      new QuadtreeNode(this.x + halfWidth, this.y + halfHeight, halfWidth, halfHeight)
    ];

    // Initialize children with current state
    this.children.forEach(child => {
      child.state = currentState;
    });

    this.state = null; // Parent nodes don't store state
  }

  /**
   * Check if a point is within this node's bounds
   */
  public contains(x: number, y: number): boolean {
    return x >= this.x && x < this.x + this.width &&
      y >= this.y && y < this.y + this.height;
  }

  /**
   * Check if this node intersects with a circle
   */
  public intersectsCircle(cx: number, cy: number, radius: number): boolean {
    // Find closest point on rectangle to circle center
    const closestX = Math.max(this.x, Math.min(cx, this.x + this.width));
    const closestY = Math.max(this.y, Math.min(cy, this.y + this.height));

    // Calculate distance from circle center to closest point
    const dx = cx - closestX;
    const dy = cy - closestY;
    const distanceSq = dx * dx + dy * dy;

    return distanceSq <= radius * radius;
  }
}

export class FogOfWarSystem {
  private scene: Scene;
  private quadtree: QuadtreeNode;
  private cellSize: number;
  private worldSize: number;
  private coordinateScale: number;
  private minCellSize: number;

  private fogPlane: Mesh | null = null;
  private fogTexture: DynamicTexture | null = null;
  private fogMaterial: StandardMaterial | null = null;

  private updateCounter: number = 0;
  private readonly UPDATE_INTERVAL = 10;
  private mergeCounter: number = 0;
  private readonly MERGE_INTERVAL = 50; // Merge every 50 updates (less frequent than visibility updates)

  constructor(scene: Scene, worldSize: number, coordinateScale: number) {
    this.scene = scene;
    this.worldSize = worldSize;
    this.coordinateScale = coordinateScale;

    // Minimum cell size for quadtree (same as original grid cell size)
    this.minCellSize = 50;
    this.cellSize = this.minCellSize;

    // Initialize quadtree covering entire world (starting at 0,0)
    this.quadtree = new QuadtreeNode(0, 0, worldSize, worldSize);
    this.quadtree.state = VisibilityState.UNEXPLORED;

    this.createFogPlane();
  }

  private createFogPlane(): void {
    const planeSize = this.worldSize / this.coordinateScale;

    // Create fog plane slightly above ground
    this.fogPlane = MeshBuilder.CreatePlane(
      "fogPlane",
      { width: planeSize, height: planeSize },
      this.scene
    );

    // Rotate to lay flat on ground
    this.fogPlane.rotation.x = -Math.PI / 2;
    // Position at origin to match world coordinates (world is centered at origin)
    this.fogPlane.position = new Vector3(planeSize / 2, 0.1, planeSize / 2);

    // Create dynamic texture for fog
    const textureSize = 512; // Can be adjusted for quality/performance
    this.fogTexture = new DynamicTexture(
      "fogTexture",
      { width: textureSize, height: textureSize },
      this.scene,
      false
    );

    this.fogTexture.hasAlpha = true;

    // Create material
    this.fogMaterial = new StandardMaterial("fogMaterial", this.scene);
    this.fogMaterial.diffuseTexture = this.fogTexture;
    this.fogMaterial.emissiveTexture = this.fogTexture;
    this.fogMaterial.useAlphaFromDiffuseTexture = true;
    this.fogMaterial.backFaceCulling = false;
    this.fogMaterial.disableLighting = true;
    this.fogMaterial.transparencyMode = 2;
    this.fogMaterial.alpha = 1;
    this.fogMaterial.alphaMode = 3;

    this.fogPlane.material = this.fogMaterial;

    // Initial texture update
    this.updateFogTexture();

    this.fogPlane.isVisible = !FULL_VISION;
  }

  /**
   * Get visibility state at world coordinates using quadtree
   */
  private getVisibilityAtNode(node: QuadtreeNode, x: number, y: number): VisibilityState {
    if (!node.contains(x, y)) {
      return VisibilityState.UNEXPLORED;
    }

    if (node.isLeaf()) {
      return node.state ?? VisibilityState.UNEXPLORED;
    }

    // Traverse to appropriate child
    if (node.children) {
      for (const child of node.children) {
        if (child.contains(x, y)) {
          return this.getVisibilityAtNode(child, x, y);
        }
      }
    }

    return VisibilityState.UNEXPLORED;
  }

  /**
   * Get visibility state at world coordinates
   */
  public getVisibility(x: number, y: number): VisibilityState {
    return FULL_VISION ? VisibilityState.VISIBLE : this.getVisibilityAtNode(this.quadtree, x, y);
  }

  /**
   * Check if a position is currently visible
   */
  public isVisible(x: number, y: number): boolean {
    return FULL_VISION || this.getVisibility(x, y) === VisibilityState.VISIBLE;
  }

  /**
   * Convert all VISIBLE cells to EXPLORED in quadtree
   */
  private convertVisibleToExplored(node: QuadtreeNode): void {
    if (node.isLeaf()) {
      if (node.state === VisibilityState.VISIBLE) {
        node.state = VisibilityState.EXPLORED;
      }
      return;
    }

    if (node.children) {
      node.children.forEach(child => this.convertVisibleToExplored(child));
    }
  }

  /**
   * Check if all children of a node have the same state and are leaves
   * If so, merge them into the parent
   */
  private tryMergeNode(node: QuadtreeNode): boolean {
    // Can't merge if node is already a leaf
    if (node.isLeaf() || !node.children) {
      return false;
    }

    // First, recursively try to merge children (bottom-up approach)
    let allChildrenMerged = true;
    for (const child of node.children) {
      if (!child.isLeaf()) {
        this.tryMergeNode(child);
        allChildrenMerged = false;
      }
    }

    // Now check if all children are leaves with the same state
    const firstChild = node.children[0];
    if (!firstChild.isLeaf()) {
      return false;
    }

    const firstState = firstChild.state ?? VisibilityState.UNEXPLORED;

    // Check if all children are leaves with the same state
    const allSameState = node.children.every(child => {
      if (!child.isLeaf()) {
        return false;
      }
      const childState = child.state ?? VisibilityState.UNEXPLORED;
      return childState === firstState;
    });

    // If all children have the same state, merge them
    if (allSameState) {
      node.state = firstState;
      node.children = null; // Remove children, making this a leaf
      return true;
    }

    return false;
  }

  /**
   * Count all nodes in the quadtree recursively
   */
  private countNodes(node: QuadtreeNode): number {
    if (node.isLeaf()) {
      return 1;
    }

    let count = 1; // Count this node
    if (node.children) {
      for (const child of node.children) {
        count += this.countNodes(child);
      }
    }
    return count;
  }

  /**
   * Merge quadtree nodes where all children have the same state
   * This helps prevent memory bloat by consolidating uniform regions
   */
  private mergeQuadtree(): void {
    const nodeCountBefore = this.countNodes(this.quadtree);
    this.tryMergeNode(this.quadtree);
    const nodeCountAfter = this.countNodes(this.quadtree);
    console.log(`[FogOfWar] Quadtree merge: ${nodeCountBefore} → ${nodeCountAfter} nodes (reduced by ${nodeCountBefore - nodeCountAfter})`);
  }

  /**
   * Set visibility state in a circular area
   */
  private setVisibilityInCircle(
    node: QuadtreeNode,
    cx: number,
    cy: number,
    radius: number,
    state: VisibilityState,
    minSize: number
  ): void {
    // If node doesn't intersect circle, skip
    if (!node.intersectsCircle(cx, cy, radius)) {
      return;
    }

    // If node is at minimum size and is a leaf, check if it's within circle and set state
    if (node.isLeaf() && node.width <= minSize && node.height <= minSize) {
      // Check if center of cell is within circle
      const centerX = node.x + node.width / 2;
      const centerY = node.y + node.height / 2;
      const dx = cx - centerX;
      const dy = cy - centerY;
      const distanceSq = dx * dx + dy * dy;

      if (distanceSq <= radius * radius) {
        node.state = state;
      }
      return;
    }

    // Node is too large or has children, split if needed and recurse
    if (node.isLeaf()) {
      node.split();
    }

    if (node.children) {
      node.children.forEach(child => {
        this.setVisibilityInCircle(child, cx, cy, radius, state, minSize);
      });
    }
  }

  /**
   * Update visibility based on units and buildings
   */
  public updateVisibility(
    myUnits: Unit3D[],
    myBuildings: Building3D[]
  ): void {
    this.updateCounter++;

    // Only do full update every N frames for performance
    if (this.updateCounter % this.UPDATE_INTERVAL !== 0) {
      return;
    }

    // First, convert all currently visible cells to explored
    this.convertVisibleToExplored(this.quadtree);

    // Then, mark areas around units as visible
    const updateForPosition = (x: number, y: number, sightRange: number) => {
      this.setVisibilityInCircle(
        this.quadtree,
        x,
        y,
        sightRange,
        VisibilityState.VISIBLE,
        this.minCellSize
      );
    };

    // Update visibility for player's units
    if (myUnits && myUnits.length > 0) {
      myUnits.forEach(unit => {
        const sightRange = this.getSightRangeForUnit(unit.k);
        updateForPosition(unit.x, unit.y, sightRange);
      });
    }

    // Update visibility for player's buildings
    if (myBuildings && myBuildings.length > 0) {
      myBuildings.forEach(building => {
        const sightRange = this.getSightRangeForBuilding(building.k);
        updateForPosition(building.x, building.y, sightRange);
      });
    }

    // Periodically merge quadtree nodes to prevent memory bloat
    this.mergeCounter++;
    if (this.mergeCounter >= this.MERGE_INTERVAL) {
      this.mergeCounter = 0;
      this.mergeQuadtree();
    }

    // Update fog texture
    this.updateFogTexture();
  }

  /**
   * Get sight range for a unit type
   */
  private getSightRangeForUnit(unitType: number): number {
    return UNIT_DEFINITIONS[unitType as UnitType]?.sightRange || 300;
  }

  /**
   * Get sight range for a building type
   */
  private getSightRangeForBuilding(buildingType: number): number {
    return BUILDING_DEFINITIONS[buildingType as BuildingType]?.sightRange || 400;
  }

  /**
   * Collect all leaf nodes from quadtree for texture rendering
   */
  private collectLeafNodes(node: QuadtreeNode, minSize: number, result: Array<{ x: number, y: number, width: number, height: number, state: VisibilityState }>): void {
    if (node.isLeaf() || (node.width <= minSize && node.height <= minSize)) {
      const state = node.state ?? VisibilityState.UNEXPLORED;
      result.push({
        x: node.x,
        y: node.y,
        width: node.width,
        height: node.height,
        state: state
      });
      return;
    }

    if (node.children) {
      node.children.forEach(child => {
        this.collectLeafNodes(child, minSize, result);
      });
    }
  }

  /**
   * Update the fog texture based on quadtree visibility
   */
  private updateFogTexture(): void {
    if (!this.fogTexture) return;

    const ctx = this.fogTexture.getContext() as CanvasRenderingContext2D;
    const textureSize = this.fogTexture.getSize();

    // Clear the canvas first with unexplored color
    ctx.fillStyle = 'rgba(255, 255, 255, 1)';
    ctx.fillRect(0, 0, textureSize.width, textureSize.height);

    // Collect all leaf nodes
    const leafNodes: Array<{ x: number, y: number, width: number, height: number, state: VisibilityState }> = [];
    this.collectLeafNodes(this.quadtree, this.minCellSize, leafNodes);

    // Map world coordinates to texture coordinates
    const worldToTextureX = (worldX: number) => (worldX / this.worldSize) * textureSize.width;
    const worldToTextureY = (worldY: number) => (worldY / this.worldSize) * textureSize.height;

    // Draw each leaf node
    leafNodes.forEach(node => {
      // Determine color based on visibility state
      let fillColor: string;
      switch (node.state) {
        case VisibilityState.UNEXPLORED:
          fillColor = 'rgba(255, 255, 255, 1)';
          break;
        case VisibilityState.EXPLORED:
          fillColor = 'rgba(200, 200, 200, 1)';
          break;
        case VisibilityState.VISIBLE:
          fillColor = 'rgba(0, 0, 0, 1)'; // Transparent
          break;
        default:
          fillColor = 'rgba(255, 255, 255, 1)';
      }

      // Convert world coordinates to texture coordinates
      const startX = Math.floor(worldToTextureX(node.x));
      const startY = Math.floor(worldToTextureY(node.y));
      const width = Math.ceil(worldToTextureX(node.width));
      const height = Math.ceil(worldToTextureY(node.height));

      ctx.fillStyle = fillColor;
      ctx.fillRect(startX, startY, width, height);
      // ctx.strokeStyle = 'rgba(0, 0, 0, 1)';
      // ctx.lineWidth = 0.2;
      // ctx.strokeRect(startX, startY, width, height);
    });

    this.fogTexture.update();
  }

  /**
   * Hide/show enemy units based on visibility
   */
  public updateEnemyVisibility(
    enemyUnits: Unit3D[],
    enemyBuildings: Building3D[]
  ): void {
    // Hide enemy units in unexplored areas
    enemyUnits.forEach(unit => {
      const visibility = this.getVisibility(unit.x, unit.y);
      if (visibility === VisibilityState.UNEXPLORED) {
        unit.mesh.isVisible = false;
      } else {
        unit.mesh.isVisible = true;
        // Optionally reduce opacity in explored areas
        if (visibility === VisibilityState.EXPLORED) {
          // Could set material alpha here if desired
        }
      }
    });

    // Hide enemy buildings in unexplored areas
    enemyBuildings.forEach(building => {
      const visibility = this.getVisibility(building.x, building.y);
      if (visibility === VisibilityState.UNEXPLORED) {
        building.mesh.isVisible = false;
      } else {
        building.mesh.isVisible = true;
      }
    });
  }

  /**
   * Cleanup
   */
  public dispose(): void {
    if (this.fogPlane) {
      this.fogPlane.dispose();
    }
    if (this.fogTexture) {
      this.fogTexture.dispose();
    }
    if (this.fogMaterial) {
      this.fogMaterial.dispose();
    }
  }
}
